<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { blogHomePath, blogLocaleFor } from '~/constants/blog';
import { FEATURE_PAGES } from '~/constants/featurePages';
import { GUIDE_PAGES } from '~/constants/guidePages';

/**
 * Footer in the template's layout: brand block with a short description and
 * social icons, three link columns, and a fourth column where the template
 * had app-store badges. We have no apps, so that slot holds the one thing a
 * visitor can do next: start a CV. The bottom hairline row carries the
 * copyright and the colour-mode switch.
 *
 * Every feature page is linked here on purpose. The navbar dropdown is
 * client-gated, so without this column the feature pages would have no
 * crawlable path from most of the site.
 */
const { t, locale } = useI18n();
const { isAuthenticated } = useAuthUser();

const year = new Date().getFullYear();

const company = computed(() => [
  { to: blogHomePath(blogLocaleFor(locale.value)), label: t('sofax.footer.blog') },
  { to: '/demo', label: t('sofax.footer.demo') },
  { to: '/pricing', label: t('sofax.footer.pricing') },
  { to: '/contact', label: t('sofax.footer.contact') },
  { to: '/affiliate', label: t('sofax.footer.affiliate') },
  { to: '/affiliate-programs', label: t('sofax.footer.affiliatePrograms') },
]);

const legal = computed(() => [
  { to: '/terms', label: t('sofax.footer.terms') },
  { to: '/privacy', label: t('sofax.footer.privacy') },
  { to: '/cookies', label: t('sofax.footer.cookies') },
  { to: '/affiliate/terms', label: t('sofax.footer.affiliateTerms') },
]);

const footerLink = 'text-base text-body transition-all duration-300 hover:font-medium hover:text-ink dark:text-gray-300 dark:hover:text-white';
</script>

<template>
  <footer class="sofax border-t border-line bg-white dark:border-gray-800 dark:bg-gray-950">
    <div class="pb-14 pt-14 lg:pt-[6.25rem]">
      <div class="shell">
        <div class="grid grid-cols-1 gap-10 sm:grid-cols-2 xl:grid-cols-[1.4fr_1fr_1fr_1fr_1.3fr] xl:gap-8">
          <!-- Brand -->
          <div class="max-w-sm sm:col-span-2 xl:col-span-1">
            <LocaleLink to="/" class="inline-block" :aria-label="t('common.logo')">
              <NuxtImg src="/logo_white.svg" :alt="t('common.logo')" width="264" height="56" class="hidden h-12 w-auto dark:block" />
              <NuxtImg src="/logo.svg" :alt="t('common.logo')" width="264" height="56" class="block h-12 w-auto dark:hidden" />
            </LocaleLink>
            <p class="my-6 text-base">{{ t('sofax.footer.about') }}</p>
            <span class="sr-only">{{ t('sofax.footer.followUs') }}</span>
            <SofaxSocialLinks />
          </div>

          <!-- Product: every feature page -->
          <nav :aria-label="t('sofax.footer.product')">
            <span class="display-heading display-heading-5 mb-6 block">{{ t('sofax.footer.product') }}</span>
            <ul class="flex flex-col gap-y-4">
              <li v-for="page in FEATURE_PAGES" :key="page.id">
                <LocaleLink :to="page.path" :class="footerLink">{{ t(page.titleKey) }}</LocaleLink>
              </li>
            </ul>
          </nav>

          <!--
            Company, with the guides stacked underneath it rather than in a
            column of their own: this column is the shortest of the three and a
            sixth column would squeeze all of them at xl. The guides need a
            crawlable sitewide link for the same reason the feature pages do.
          -->
          <div class="flex flex-col gap-10">
          <nav :aria-label="t('sofax.footer.company')">
            <span class="display-heading display-heading-5 mb-6 block">{{ t('sofax.footer.company') }}</span>
            <ul class="flex flex-col gap-y-4">
              <li v-for="link in company" :key="link.to">
                <LocaleLink :to="link.to" :class="footerLink">{{ link.label }}</LocaleLink>
              </li>
            </ul>
          </nav>

          <nav :aria-label="t('sofax.footer.guides')">
            <span class="display-heading display-heading-5 mb-6 block">{{ t('sofax.footer.guides') }}</span>
            <ul class="flex flex-col gap-y-4">
              <li v-for="page in GUIDE_PAGES" :key="page.id">
                <LocaleLink :to="page.path" :class="footerLink">{{ t(page.titleKey) }}</LocaleLink>
              </li>
              <!-- English only, one URL (the tools for UK applicants) -->
              <li>
                <LocaleLink to="/civil-service-application" :class="footerLink">{{ t('sofax.footer.civilService') }}</LocaleLink>
              </li>
              <li>
                <LocaleLink to="/nhs-supporting-statement" :class="footerLink">{{ t('sofax.footer.nhsStatement') }}</LocaleLink>
              </li>
            </ul>
          </nav>
          </div>

          <!-- Legal -->
          <nav :aria-label="t('sofax.footer.legal')">
            <span class="display-heading display-heading-5 mb-6 block">{{ t('sofax.footer.legal') }}</span>
            <ul class="flex flex-col gap-y-4">
              <li v-for="link in legal" :key="link.to">
                <LocaleLink :to="link.to" :class="footerLink">{{ link.label }}</LocaleLink>
              </li>
            </ul>
          </nav>

          <!-- Where the template shows store badges: start a CV -->
          <div class="sm:col-span-2 xl:col-span-1">
            <span class="display-heading display-heading-5 mb-6 block">{{ t('sofax.footer.getStarted') }}</span>
            <p class="mb-6 text-base">{{ t('sofax.footer.getStartedText') }}</p>
            <SofaxBtn
              :to="isAuthenticated ? '/cv' : '/free-cv-builder'"
              :label="isAuthenticated ? t('sofax.nav.myCvs') : t('sofax.footer.cta')"
              variant="dark"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="shell">
      <div class="flex flex-col items-center justify-between gap-4 border-t border-line py-9 text-center text-base sm:flex-row sm:text-left dark:border-gray-800">
        <div>
          © {{ year }} <a class="link" href="https://www.inmyresume.com/">InMyResume.com</a>. {{ t('sofax.footer.rights') }}
          <!-- Directory badge: the listing asks for this exact link back -->
          · <a class="link" href="https://zplatform.ai/" target="_blank" rel="noopener" title="Featured on zPlatform.ai">Featured on zPlatform.ai</a>
        </div>
        <div class="flex flex-wrap items-center justify-center gap-4">
          <LanguageSwitcher />
          <NavbarColorModeSwitcher />
        </div>
      </div>
    </div>
  </footer>
</template>
